import {
  addComment,
  createGroupPost,
  createGroupSyllabus,
  createGroupThread,
  createPost,
  createSyllabus,
  createThread,
  deleteComment,
  deleteGroupPost,
  deleteGroupSyllabus,
  deleteGroupThread,
  deletePost,
  deleteSyllabus,
  deleteThread,
  downloadSyllabusPdf,
  fetchBoard as fetchBoardSummary,
  fetchGroupOverview,
  fetchGroupPosts,
  fetchGroupSyllabus,
  fetchGroupThreads,
  fetchPosts,
  fetchSyllabus,
  fetchThreads,
  saveGroupOverview,
  saveOverview,
  updateGroupPost,
  updateGroupSyllabus,
  updatePost,
  updateSyllabus,
} from "../../services/montessoriCourseBoard.service";

/**
 * One uniform API for the four board tabs, whichever scope they are rendering.
 *
 * The tabs are shared between a branch's class board and the Group Console's
 * cross-branch board. Rather than forking them, each tab takes the object this
 * returns and never imports a service function directly — so the only
 * difference between the two screens is which endpoints get called and which
 * react-query keys are used.
 *
 * `group` scope has no curriculum download (the export is a branch-level PDF of
 * one class's plan) and no replies (a group admin reads replies per branch but
 * does not post them), so those come back null and the tabs hide the control.
 */
export default function getBoardApi({ scope, classId, className, groupId }) {
  if (scope === "group") {
    const root = ["montessori-group-board", groupId];

    return {
      scope: "group",
      enabled: !!groupId,
      keys: {
        board: root,
        posts: [...root, "posts"],
        syllabus: [...root, "syllabus"],
        threads: [...root, "threads"],
      },

      fetchBoard: () => fetchGroupOverview(groupId),
      saveOverview: (overview) => saveGroupOverview(groupId, overview),

      fetchPosts: () => fetchGroupPosts(groupId),
      createPost: (payload) => createGroupPost(groupId, payload),
      updatePost: (id, payload) => updateGroupPost(groupId, id, payload),
      deletePost: (id) => deleteGroupPost(groupId, id),

      fetchSyllabus: () => fetchGroupSyllabus(groupId),
      createSyllabus: (payload) => createGroupSyllabus(groupId, payload),
      updateSyllabus: (id, payload) => updateGroupSyllabus(groupId, id, payload),
      deleteSyllabus: (id) => deleteGroupSyllabus(groupId, id),
      downloadSyllabus: null,

      fetchThreads: () => fetchGroupThreads(groupId),
      createThread: (payload) => createGroupThread(groupId, payload),
      deleteThread: (id) => deleteGroupThread(groupId, id),
      addComment: null,
      deleteComment: null,
    };
  }

  const root = ["montessori-board", classId];

  return {
    scope: "class",
    enabled: !!classId,
    keys: {
      board: root,
      posts: ["montessori-board-posts", classId],
      syllabus: ["montessori-board-syllabus", classId],
      threads: ["montessori-board-threads", classId],
    },

    fetchBoard: () => fetchBoardSummary(classId),
    saveOverview: (overview) => saveOverview(classId, overview),

    fetchPosts: () => fetchPosts(classId),
    createPost: (payload) => createPost(classId, payload),
    updatePost: (id, payload) => updatePost(id, payload),
    deletePost: (id) => deletePost(id),

    fetchSyllabus: () => fetchSyllabus(classId),
    createSyllabus: (payload) => createSyllabus(classId, payload),
    updateSyllabus: (id, payload) => updateSyllabus(id, payload),
    deleteSyllabus: (id) => deleteSyllabus(id),
    downloadSyllabus: () => downloadSyllabusPdf(classId, className),

    fetchThreads: () => fetchThreads(classId),
    createThread: (payload) => createThread(classId, payload),
    deleteThread: (id) => deleteThread(id),
    addComment: (threadId, comment) => addComment(threadId, comment, classId),
    deleteComment: (id) => deleteComment(id),
  };
}
